import { NavItem } from './nav-item/nav-item';

/**
 * Définition centralisée des items de menu Dinki, scoppés par rôle.
 *
 * Rôles disponibles (cf. {@code auth.service.ts > UserType}) :
 * <ul>
 *   <li>{@code ADMIN}    — administrateur plateforme (super-admin)</li>
 *   <li>{@code BUSINESS} — propriétaire ou gérant d'atelier couture</li>
 *   <li>{@code CUSTOMER} — client final d'un atelier (mode "espace client")</li>
 *   <li>{@code PARTNER}  — partenaire / affilié</li>
 *   <li>{@code APP}      — rôle générique (multi-tenant, rarement seul)</li>
 * </ul>
 *
 * Convention : un item sans {@code roles} est visible par tous les rôles
 * (utile pour les sections "Compte" en bas de menu).
 *
 * <p><b>Internationalisation</b> — les valeurs {@code displayName} et
 * {@code navCap} sont des <i>clés de traduction</i> résolues par ngx-translate
 * dans {@code nav-item.component.html}. Pour ajouter un menu : créer la clé
 * dans {@code assets/i18n/dinki-{lang}.json} sous {@code nav.*}.</p>
 *
 * Filtrage effectif : {@link AppSidebarComponent#refreshNavItems} se charge
 * d'écarter les items non autorisés selon le rôle courant.
 */
export const navItems: NavItem[] = [
  // ═══════════════════════════════════════════════════════════════════════
  // BUSINESS — Atelier couture (cœur métier Dinki)
  // ═══════════════════════════════════════════════════════════════════════
  { divider: true, navCap: 'nav.section_dinki', roles: ['ADMIN', 'BUSINESS'] },
  { navCap: 'nav.section_activity', roles: ['ADMIN', 'BUSINESS'] },
  {
    displayName: 'nav.dashboard',
    iconName: 'solar:home-smile-line-duotone',
    route: '/dinki/dashboard',
    roles: ['ADMIN', 'BUSINESS'],
  },
  {
    displayName: 'nav.stats',
    iconName: 'solar:graph-up-line-duotone',
    route: '/dinki/statistiques',
    roles: ['BUSINESS'],
  },

  // ─── Production atelier ──────────────────────────────────────────────
  { divider: true, navCap: 'nav.section_atelier', roles: ['BUSINESS'] },
  {
    displayName: 'nav.clients',
    iconName: 'solar:users-group-two-rounded-line-duotone',
    route: '/dinki/clients',
    roles: ['BUSINESS'],
  },
  {
    displayName: 'nav.mesures',
    iconName: 'solar:ruler-line-duotone',
    route: '/dinki/mesures',
    roles: ['BUSINESS'],
  },
  {
    displayName: 'nav.modeles',
    iconName: 'solar:palette-line-duotone',
    route: '/dinki/modeles',
    roles: ['BUSINESS'],
  },
  {
    displayName: 'nav.commandes',
    iconName: 'solar:bag-3-line-duotone',
    route: '/dinki/commandes',
    roles: ['BUSINESS'],
  },
  {
    displayName: 'nav.board',
    iconName: 'solar:checklist-line-duotone',
    route: '/dinki/commandes/board',
    roles: ['BUSINESS'],
  },
  {
    displayName: 'nav.inventaire',
    iconName: 'solar:box-line-duotone',
    route: '/dinki/inventaire',
    roles: ['BUSINESS'],
    feature: 'FEATURE_INVENTAIRE',
  },

  // ─── Finances / comptabilité (module activable via FEATURE_FACTURATION) ──
  {
    divider: true,
    navCap: 'nav.section_finances',
    roles: ['ADMIN', 'BUSINESS'],
    feature: 'FEATURE_FACTURATION',
  },
  {
    displayName: 'nav.finances',
    iconName: 'solar:wallet-line-duotone',
    route: '/dinki/finances',
    roles: ['ADMIN', 'BUSINESS'],
    feature: 'FEATURE_FACTURATION',
  },
  {
    displayName: 'nav.factures',
    iconName: 'solar:document-text-line-duotone',
    route: '/dinki/factures',
    roles: ['ADMIN', 'BUSINESS'],
    feature: 'FEATURE_FACTURATION',
  },
  {
    displayName: 'nav.depenses',
    iconName: 'solar:cash-out-line-duotone',
    route: '/dinki/depenses',
    roles: ['ADMIN', 'BUSINESS'],
    feature: 'FEATURE_FACTURATION',
  },

  // ─── Configuration atelier ───────────────────────────────────────────
  { divider: true, navCap: 'nav.section_configuration', roles: ['ADMIN', 'BUSINESS'] },
  {
    displayName: 'nav.atelier',
    iconName: 'solar:shop-line-duotone',
    route: '/dinki/atelier',
    roles: ['BUSINESS'],
  },
  {
    displayName: 'nav.atelier_identity',
    iconName: 'solar:document-line-duotone',
    route: '/dinki/atelier/identite',
    roles: ['BUSINESS'],
  },
  {
    displayName: 'nav.atelier_team',
    iconName: 'solar:users-group-rounded-line-duotone',
    route: '/dinki/atelier/equipe',
    roles: ['BUSINESS'],
  },
  {
    displayName: 'nav.atelier_profiles',
    iconName: 'solar:shield-user-line-duotone',
    route: '/dinki/atelier/profils',
    roles: ['BUSINESS'],
  },
  {
    displayName: 'nav.subscription_invoices',
    iconName: 'solar:bill-list-line-duotone',
    route: '/dinki/facturation/abonnement',
    roles: ['BUSINESS'],
  },
  {
    displayName: 'nav.abonnement',
    iconName: 'solar:crown-line-duotone',
    route: '/dinki/abonnement',
    roles: ['BUSINESS'],
  },
  {
    displayName: 'nav.sync',
    iconName: 'solar:refresh-line-duotone',
    route: '/dinki/sync',
    roles: ['BUSINESS'],
  },
  {
    displayName: 'nav.parametres',
    iconName: 'solar:settings-line-duotone',
    route: '/dinki/parametres',
    roles: ['ADMIN', 'BUSINESS'],
  },

  // ═══════════════════════════════════════════════════════════════════════
  // ADMIN — Administration plateforme
  // ═══════════════════════════════════════════════════════════════════════
  { divider: true, navCap: 'nav.section_administration', roles: ['ADMIN'] },
  { navCap: 'nav.section_platform', roles: ['ADMIN'] },
  {
    displayName: 'nav.admin_overview',
    iconName: 'solar:chart-square-line-duotone',
    route: '/dinki/admin/overview',
    roles: ['ADMIN'],
  },
  {
    displayName: 'nav.admin_users',
    iconName: 'solar:users-group-rounded-line-duotone',
    route: '/dinki/admin/utilisateurs',
    roles: ['ADMIN'],
  },
  {
    displayName: 'nav.admin_countries',
    iconName: 'solar:global-line-duotone',
    route: '/dinki/admin/pays',
    roles: ['ADMIN'],
  },
  {
    displayName: 'nav.admin_ateliers',
    iconName: 'solar:shop-line-duotone',
    route: '/dinki/admin/ateliers',
    roles: ['ADMIN'],
  },
  {
    displayName: 'nav.admin_plans',
    iconName: 'solar:crown-line-duotone',
    route: '/dinki/admin/plans',
    roles: ['ADMIN'],
  },
  {
    displayName: 'nav.admin_billing',
    iconName: 'solar:dollar-line-duotone',
    route: '/dinki/admin/billing',
    roles: ['ADMIN'],
  },
  {
    displayName: 'nav.admin_subscriptions',
    iconName: 'solar:ticket-line-duotone',
    route: '/dinki/admin/subscriptions',
    roles: ['ADMIN'],
  },
  {
    displayName: 'nav.admin_payment_methods',
    iconName: 'solar:wallet-money-line-duotone',
    route: '/dinki/admin/payment-methods',
    roles: ['ADMIN'],
  },
  {
    displayName: 'nav.admin_profiles',
    iconName: 'solar:shield-user-line-duotone',
    route: '/dinki/admin/profils',
    roles: ['ADMIN'],
  },

  // ═══════════════════════════════════════════════════════════════════════
  // CUSTOMER — Espace client final
  // ═══════════════════════════════════════════════════════════════════════
  { divider: true, navCap: 'nav.section_my_space', roles: ['CUSTOMER'] },
  {
    displayName: 'nav.home',
    iconName: 'solar:home-smile-line-duotone',
    route: '/dinki/dashboard',
    roles: ['CUSTOMER'],
  },
  {
    displayName: 'nav.my_measurements',
    iconName: 'solar:ruler-line-duotone',
    route: '/dinki/mes-mesures',
    roles: ['CUSTOMER'],
  },
  {
    displayName: 'nav.my_orders',
    iconName: 'solar:bag-3-line-duotone',
    route: '/dinki/mes-commandes',
    roles: ['CUSTOMER'],
  },
  {
    displayName: 'nav.my_invoices',
    iconName: 'solar:document-text-line-duotone',
    route: '/dinki/mes-factures',
    roles: ['CUSTOMER'],
  },

  // ═══════════════════════════════════════════════════════════════════════
  // PARTNER — Programme d'affiliation
  // ═══════════════════════════════════════════════════════════════════════
  { divider: true, navCap: 'nav.section_partnership', roles: ['PARTNER'] },
  {
    displayName: 'nav.partner',
    iconName: 'solar:hand-shake-line-duotone',
    route: '/dinki/partenariat',
    roles: ['PARTNER'],
  },
  {
    displayName: 'nav.commissions',
    iconName: 'solar:wallet-money-line-duotone',
    route: '/dinki/partenariat/commissions',
    roles: ['PARTNER'],
  },
  {
    displayName: 'nav.referrals',
    iconName: 'solar:users-group-rounded-line-duotone',
    route: '/dinki/partenariat/filleuls',
    roles: ['PARTNER'],
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Compte — commun à tous les rôles connectés
  // ═══════════════════════════════════════════════════════════════════════
  { divider: true, navCap: 'nav.section_account' },
  {
    displayName: 'nav.profile',
    iconName: 'solar:user-circle-line-duotone',
    route: '/dinki/profil',
  },
  {
    displayName: 'nav.notifications',
    iconName: 'solar:bell-line-duotone',
    route: '/dinki/notifications',
  },
];
